import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import { ConsoleCapabilityProvider } from '@/auth/consoleCapability';
import { useCan } from '@/auth/consoleCapabilityContext';
import { ActingOrgProvider } from '@/auth/actingOrg';
import { ConsoleBreadcrumbProvider } from '@/components/shell/ConsoleBreadcrumbContext';
import { fetchMeAdmin, fetchOrganizations, OrgAdminUnauthorizedError } from '@/api/orgAdmin';
import { fetchRoleMapSyncStatus } from '@/api/console';
import { AppBar } from '@/components/AppBar';
import { ActingOrgPicker } from '@/components/ActingOrgPicker';
import { ConsoleTopbar } from '@/components/shell/ConsoleTopbar';
import { DASHBOARD_GATE_CAPABILITIES } from '@/pages/Home/useDashboard';
import styles from './ConsoleLayout.module.css';

/**
 * EAA-1776 (B5 slice 1): `/console` shell — left nav + outlet.
 *
 * Fetches `GET /me/admin` once and mounts `ConsoleCapabilityProvider` with the
 * server-computed `capabilities[]` (§1b: the FE's ONLY permission source).
 * Nav items render per `can('console.*.view')`; an empty set redirects to `/`
 * (a plain user deep-linking `/console/*` never sees the shell — and the API
 * answers 403 regardless: hiding is UX, never security).
 *
 * EAA-1778: presentation pass — AppBar + nav icons + brand. Gating unchanged.
 *
 * EAA-1813 (canonical shell — handoff frame 09 "Inset shell home"): the
 * console no longer sits below a full-width `AppBar`. The rounded content
 * panel now carries its own in-panel topbar (`ConsoleTopbar`) and the
 * sidebar sits directly on the shell's own canvas tone — `AppBar` stays
 * exactly as it was, but only the loading/error fallbacks below (which have
 * no sidebar to anchor a panel to yet) still render it.
 *
 * EAA-1814 (IA change — `/` moves inside this shell, see App.tsx's own
 * comment on the route move): the redirect-to-`/` comment above stopped being
 * a full fix the moment `/` became a route INSIDE `ConsoleLayout` rather than
 * a sibling of it — redirecting a capability-less caller to `/` no longer
 * escapes the shell, it just re-enters the same layout on the same route, so
 * the full sidebar/nav kept rendering around `LauncherPage`'s frame-11 plain
 * hero. The capability-less early return below (before the shell JSX) is
 * what actually removes the sidebar/nav now; the redirect effect still
 * matters for a capability-less caller deep-linking `/console/*` directly.
 */

/**
 * EAA-1814: `capability` (single, AND-gated by `can()`) is the shape every nav
 * item used before this ticket. The new "Bảng điều khiển" item has no
 * dedicated `console.home.*` capability to check (CLAUDE.md rule — restated
 * on `DASHBOARD_GATE_CAPABILITIES`'s own docstring), so `anyOf` is an
 * alternative OR-gated list: visible when the caller holds AT LEAST ONE of
 * the listed capabilities. Exactly one of the two is ever set per item.
 */
type NavItem = { to: string; label: string; icon: ReactNode; end?: boolean } & (
    | { capability: string; anyOf?: undefined }
    | { capability?: undefined; anyOf: readonly string[] }
);

/** Mirrors the OR/AND distinction on `NavItem` above. */
function canSeeNavItem(item: NavItem, can: (key: string) => boolean): boolean {
    return item.anyOf ? item.anyOf.some(can) : can(item.capability);
}

/** EAA-1813: desktop icon-rail collapse preference, persisted per the S1 spec.
 *  EAA-1846 (frame 95): a SECOND key records whether the user ever actually
 *  pressed the toggle. Until they have, the rail follows a smart per-route
 *  default (collapsed everywhere except Home) rather than one fixed value —
 *  see `ConsoleLayout`'s `navCollapsed` state below for why that needs two
 *  booleans, not one. */
const NAV_COLLAPSE_STORAGE_KEY = 'portal.console.navCollapsed';
const NAV_COLLAPSE_EXPLICIT_KEY = 'portal.console.navCollapsed.explicit';
/** Frame 95: "hẹp hơn 1280px" auto-collapses and locks the toggle. */
const RAIL_AUTO_COLLAPSE_QUERY = '(max-width: 1280px)';

function readStoredNavCollapsed(): boolean | null {
    try {
        if (window.localStorage.getItem(NAV_COLLAPSE_EXPLICIT_KEY) !== '1') return null;
        return window.localStorage.getItem(NAV_COLLAPSE_STORAGE_KEY) === '1';
    } catch {
        // Private-mode / storage-disabled — no explicit preference to honor.
        return null;
    }
}

/**
 * EAA-1804: the nav is grouped rather than one flat list of eight links — at that
 * length a flat list gives no sense of which screens belong together, and the
 * console spans three unrelated concerns (org shape, app wiring, operations).
 * A group renders only when the capability filter leaves it at least one item,
 * so gating still drives visibility and an empty group never leaves a dangling
 * heading.
 *
 * EAA-1813: group labels/order now match the canonical frame's four headings
 * verbatim — "Tổng quan" (previously unlabeled) and "Ứng dụng & quyền" (was
 * "Ứng dụng và quyền" — frame 09 line 543 uses the ampersand, not "và").
 */
// EAA-1845: `label`/`group.label` below hold i18n KEYS, not literal text — this
// array is built at module scope (outside any component), where the `t()`
// translation function isn't available. `ConsoleNav` resolves each key to
// display text via `t(item.label)` / `t(group.label)` at render time.
const NAV_GROUPS: { label?: string; items: NavItem[] }[] = [
    {
        label: 'console.nav.groupOverview',
        items: [
            {
                to: '/',
                label: 'console.nav.itemDashboard',
                // EAA-1814: gate visibility on the SAME "any KPI capability"
                // signal LauncherPage's dashboard region uses, so the nav
                // entry and the page it links to agree on who sees a
                // populated dashboard vs the frame-11 plain-user hero.
                anyOf: DASHBOARD_GATE_CAPABILITIES,
                icon: <HomeIcon />,
                end: true,
            },
            {
                to: '/console',
                label: 'console.nav.itemDirectory',
                capability: 'console.directory.view',
                icon: <DirectoryIcon />,
                end: true,
            },
        ],
    },
    {
        label: 'console.nav.groupOrg',
        items: [
            // Tổ chức = the existing EAA-1764 screens mounted in this shell (the org
            // management CANVAS is ⏸ on hold — see PLAN-portal-admin-console §1).
            {
                to: '/organizations',
                label: 'console.nav.itemOrg',
                capability: 'console.orgs.view',
                icon: <BuildingIcon />,
            },
            // EAA-1798 (P4c): the acting org's hierarchy (tenants + levels + assignments).
            {
                to: '/console/hierarchy',
                label: 'console.nav.itemHierarchy',
                capability: 'console.orgs.view',
                icon: <TreeIcon />,
            },
        ],
    },
    {
        label: 'console.nav.groupAppsAndPermissions',
        items: [
            {
                to: '/console/apps',
                label: 'console.nav.itemApps',
                capability: 'console.apps.view',
                icon: <GridIcon />,
            },
            {
                to: '/console/role-map',
                // EAA-1814: the handoff glossary bans both "Role map" and "Ánh xạ" from visible
                // copy — "ánh xạ" is the maths word for *mapping*, which tells a school
                // administrator nothing. Display strings only; the route and the RoleMap model
                // keep their names.
                // EAA-1841: ONE item again. There were briefly two — this one relabeled
                // "Quy đổi vai trò" so it could sit beside a separate "/console/role-coverage".
                // That relabel was itself a glossary violation ("Quy đổi" is banned in the same
                // handoff table that bans "Ánh xạ"), and the two routes turned out to be two
                // views of one screen (design frames 42 and 49 share this very title). The
                // coverage board is now this screen's "Bảng phủ" view.
                label: 'console.nav.itemRoleMap',
                capability: 'console.rolemap.view',
                icon: <MapIcon />,
            },
        ],
    },
    {
        label: 'console.nav.groupOperations',
        items: [
            {
                to: '/console/accounts',
                label: 'console.nav.itemAccounts',
                capability: 'console.accounts.issue',
                icon: <UserIcon />,
            },
            {
                to: '/console/audit',
                label: 'console.nav.itemAudit',
                capability: 'console.audit.view',
                icon: <ListIcon />,
            },
            {
                to: '/console/config',
                label: 'console.nav.itemConfig',
                capability: 'console.config.view',
                icon: <GearIcon />,
            },
            // ECR-85: Kho lưu trữ — platform-wide storage spaces and quota. Super-admin
            // only today (portal-api grants `console.storage.*` to no other role).
            {
                to: '/console/storage',
                label: 'console.nav.itemStorage',
                capability: 'console.storage.view',
                icon: <StorageIcon />,
            },
            // EAA-1842: both built-in roles hold `console.permissions.view`, so
            // this is the one Vận hành entry an org-admin also sees — deliberately,
            // since understanding your own ceiling is what makes a 403 actionable.
            {
                to: '/console/permissions',
                label: 'console.nav.itemPermissions',
                capability: 'console.permissions.view',
                icon: <ListIcon />,
            },
        ],
    },
];

/**
 * EAA-1813: trailing nav counts. Frame 09 renders these two asymmetrically —
 * "Danh sách tổ chức" is a plain muted number with no pill (handoff line 536
 * has no background/radius at all), "Liên kết vai trò" IS a real Badge-count
 * pill (line 551 matches COMPONENTS.md's Badge count variant exactly: 18px
 * min-size, pill radius, danger fill, 11px/700). Kept that asymmetry rather
 * than forcing both into pills.
 *
 * Never a placeholder number: both return nothing until real data resolves.
 */
function navBadgeFor(
    to: string,
    orgsCount: number | undefined,
    roleMapIssueCount: number | undefined
): { node: ReactNode; count?: number } {
    if (to === '/organizations' && typeof orgsCount === 'number') {
        return {
            count: orgsCount,
            node: (
                <span className="portal-console-nav-count" aria-hidden="true">
                    {orgsCount}
                </span>
            ),
        };
    }
    if (
        to === '/console/role-map' &&
        typeof roleMapIssueCount === 'number' &&
        roleMapIssueCount > 0
    ) {
        return {
            count: roleMapIssueCount,
            node: (
                <span
                    className="portal-console-nav-count portal-console-nav-count--danger"
                    aria-hidden="true"
                >
                    {roleMapIssueCount > 99 ? '99+' : roleMapIssueCount}
                </span>
            ),
        };
    }
    return { node: null };
}

function ConsoleNav({
    onNavigate,
    orgsCount,
    roleMapIssueCount,
    navCollapsed,
    onToggleCollapse,
    collapseLocked,
}: {
    onNavigate?: () => void;
    /** Total orgs the caller administers — undefined while loading/disabled. */
    orgsCount?: number;
    /** Paused role-map links + orphan Levels — anything currently blocking
     *  provisioning, bundled the same way the home KPI card bundles them
     *  (see HomeKpiStrip / frame 09's "Liên kết vai trò" tile). Undefined
     *  while loading/disabled. */
    roleMapIssueCount?: number;
    navCollapsed: boolean;
    onToggleCollapse: () => void;
    /** EAA-1846 (frame 95): below 1280px the rail auto-collapses and the
     *  toggle is locked — hidden entirely, not shown disabled, since a
     *  visible-but-inert button invites a click that does nothing. */
    collapseLocked: boolean;
}) {
    const can = useCan();
    const { t } = useTranslation();
    const groups = NAV_GROUPS.map((group) => ({
        ...group,
        items: group.items.filter((item) => canSeeNavItem(item, can)),
    })).filter((group) => group.items.length > 0);

    return (
        <nav className="portal-console-nav" aria-label={t('console.nav.regionLabel')}>
            {/* EAA-1813: the acting-org card is the sidebar's top identity now —
                frame 09 has no separate "Console" brand row above it.
                EAA-1846 (frame 95 "Vỏ chuẩn"): the collapse toggle moved BACK
                here, to the bottom of the rail — "Nút ở đây chứ không ở thanh
                trên — thanh trên thuộc về trang, rail thuộc về điều hướng"
                (the button belongs here, not the topbar — the topbar belongs
                to the page, the rail belongs to navigation). Reverses the
                EAA-1813 placement in ConsoleTopbar, which was correct against
                the older frame 09 reference at the time. */}
            <ActingOrgPicker variant={navCollapsed ? 'rail' : 'card'} />
            {groups.map((group, index) => (
                <div className="portal-console-nav-group" key={group.label ?? `group-${index}`}>
                    {group.label && (
                        <p className="portal-console-nav-group-label">{t(group.label)}</p>
                    )}
                    {group.items.map((item) => {
                        const badge = navBadgeFor(item.to, orgsCount, roleMapIssueCount);
                        const itemLabel = t(item.label);
                        const accessibleLabel = badge.count
                            ? `${itemLabel} (${badge.count})`
                            : itemLabel;
                        return (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.end}
                                onClick={onNavigate}
                                aria-label={accessibleLabel}
                                title={itemLabel}
                                className={({ isActive }) =>
                                    'portal-console-nav-link' + (isActive ? ' is-active' : '')
                                }
                            >
                                <span className="portal-console-nav-icon" aria-hidden="true">
                                    {item.icon}
                                </span>
                                <span className="portal-console-nav-label">{itemLabel}</span>
                                {badge.node}
                            </NavLink>
                        );
                    })}
                </div>
            ))}
            <NavLink
                to="/"
                onClick={onNavigate}
                aria-label={t('console.nav.backHomeAria')}
                title={t('console.nav.backHomeAria')}
                className="portal-console-nav-link portal-console-nav-home"
            >
                <span className="portal-console-nav-icon" aria-hidden="true">
                    <HomeIcon />
                </span>
                <span className="portal-console-nav-label">{t('console.nav.backHomeLabel')}</span>
            </NavLink>
            {!collapseLocked && (
                <button
                    type="button"
                    className="portal-console-nav-link portal-console-nav-collapse-btn"
                    onClick={onToggleCollapse}
                    aria-pressed={navCollapsed}
                    title={
                        navCollapsed
                            ? t('console.nav.expandRailAria')
                            : t('console.nav.collapseRailAria')
                    }
                >
                    <span className="portal-console-nav-icon" aria-hidden="true">
                        {navCollapsed ? <RailExpandIcon /> : <RailCollapseIcon />}
                    </span>
                    <span className="portal-console-nav-label">
                        {t('console.nav.collapseRailLabel')}
                    </span>
                    <kbd className="portal-console-nav-collapse-kbd" aria-hidden="true">
                        [
                    </kbd>
                </button>
            )}
        </nav>
    );
}

export function ConsoleLayout() {
    const config = useRuntimeConfig();
    const { t } = useTranslation();
    const { authenticated, accessToken, reauthenticate, status } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    // EAA-1804: below the 720px breakpoint the nav is an off-canvas drawer rather
    // than a wrapped row of eight links, which used to push the page content down
    // by several lines before anything useful was visible.
    const [navOpen, setNavOpen] = useState(false);
    const closeNav = useCallback(() => setNavOpen(false), []);

    // EAA-1813: desktop icon-rail (72px) toggle, independent of the mobile
    // drawer above; the mobile drawer ignores this (see the `--rail` reset in
    // global.css) since collapsing an off-canvas overlay has no meaning.
    //
    // EAA-1846 (frame 95): "Ghi nhớ: trạng thái theo người dùng, giữ nguyên
    // khi đổi trang... không tự bung khi vào trang chủ nếu người dùng đã thu
    // gọn" — once the user has EVER pressed the toggle, that value is fixed
    // and route-independent. Until then there is no explicit preference to
    // honor, so the rail follows a smart per-route default instead: collapsed
    // on every working page, expanded on Home — which is why this cannot be a
    // single persisted boolean the way it was before this ticket.
    const storedExplicit = readStoredNavCollapsed();
    const [hasExplicitPreference, setHasExplicitPreference] = useState(storedExplicit !== null);
    const [explicitCollapsed, setExplicitCollapsed] = useState(storedExplicit ?? false);
    const isHome = location.pathname === '/';

    const [collapseLocked, setCollapseLocked] = useState(false);
    useEffect(() => {
        const mql = window.matchMedia(RAIL_AUTO_COLLAPSE_QUERY);
        const apply = () => setCollapseLocked(mql.matches);
        apply();
        mql.addEventListener('change', apply);
        return () => mql.removeEventListener('change', apply);
    }, []);

    // Frame 95: below 1280px the rail auto-collapses AND the toggle locks in
    // that state — `collapseLocked` wins over any explicit preference.
    const navCollapsed = collapseLocked || (hasExplicitPreference ? explicitCollapsed : !isHome);

    const toggleNavCollapsed = useCallback(() => {
        setHasExplicitPreference(true);
        setExplicitCollapsed(!navCollapsed);
        try {
            window.localStorage.setItem(NAV_COLLAPSE_EXPLICIT_KEY, '1');
        } catch {
            // Best-effort persistence only — private mode / quota errors are not fatal.
        }
    }, [navCollapsed]);

    useEffect(() => {
        if (!hasExplicitPreference) return;
        try {
            window.localStorage.setItem(NAV_COLLAPSE_STORAGE_KEY, explicitCollapsed ? '1' : '0');
        } catch {
            // Best-effort persistence only — private mode / quota errors are not fatal.
        }
    }, [hasExplicitPreference, explicitCollapsed]);

    // Frame 95: keyboard shortcut `[` toggles the rail — guarded off text
    // inputs/textareas/contenteditable so typing a literal `[` never fires it,
    // and off while the rail is locked (nothing to toggle).
    useEffect(() => {
        if (collapseLocked) return;
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key !== '[' || event.metaKey || event.ctrlKey || event.altKey) return;
            const target = event.target as HTMLElement | null;
            const tag = target?.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable) return;
            toggleNavCollapsed();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [collapseLocked, toggleNavCollapsed]);

    // Close on route change too, not just on link click — a browser back/forward
    // navigation would otherwise leave the drawer covering the new page.
    useEffect(() => {
        setNavOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        if (!navOpen) return;
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setNavOpen(false);
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [navOpen]);

    const enabled = authenticated && Boolean(accessToken);
    const meAdminQuery = useQuery({
        queryKey: ['me-admin', accessToken],
        queryFn: () => fetchMeAdmin(config.apiBase, accessToken as string),
        enabled,
        retry: false,
    });

    // Absent field (pre-EAA-1775 portal-api) degrades to [] — fail-closed.
    const capabilities = meAdminQuery.data?.capabilities ?? [];
    const canOrgs = capabilities.includes('console.orgs.view');
    const canRoleMap = capabilities.includes('console.rolemap.view');
    const dataEnabled = enabled && meAdminQuery.isSuccess;

    /**
     * EAA-1813: nav trailing counts (frame 09 items 4). Query keys deliberately
     * match `HomeKpiStrip`'s ('home-orgs-count' / 'home-rolemap-status') so a
     * caller arriving from the launcher reuses its already-cached response
     * instead of firing a duplicate request — same "share one fetch per
     * source" rule that file's own docstring states.
     *
     * EAA-1818: `parentId: null` counts ROOT organizations only, and the key
     * carries it. This badge sits on the link to `/organizations`, which is the
     * org REGISTRY and lists roots — a bare `fetchOrganizations()` omits
     * `parent_id` entirely and so counted every `Organization` at every depth,
     * making the badge disagree with the screen it points at (5 vs 2 once a tree
     * had child đơn vị in it). Child đơn vị are counted per-organization on that
     * screen's own "Đơn vị" column instead.
     */
    const orgsCountQuery = useQuery({
        queryKey: ['home-orgs-count', accessToken, 'roots'],
        queryFn: () =>
            fetchOrganizations(config.apiBase, accessToken as string, { parentId: null }),
        enabled: dataEnabled && canOrgs,
        retry: false,
        staleTime: 60_000,
    });
    const roleMapStatusQuery = useQuery({
        queryKey: ['home-rolemap-status', accessToken],
        queryFn: () => fetchRoleMapSyncStatus(config.apiBase, accessToken as string),
        enabled: dataEnabled && canRoleMap,
        retry: false,
        staleTime: 60_000,
    });
    const orgsCount = orgsCountQuery.data?.count;
    const roleMapIssueCount = roleMapStatusQuery.data
        ? roleMapStatusQuery.data.paused_count + roleMapStatusQuery.data.orphan_level_count
        : undefined;

    useEffect(() => {
        // EAA-1825: was `client.login(...)` — an unconditional redirect on every
        // 401, which turns an API that refuses a perfectly valid bearer into an
        // endless reload (Keycloak re-mints, /me/admin 401s again, repeat).
        // `reauthenticate` is the loop-guarded entry point: it still re-signs-in
        // for a genuinely ended session, but gives up and shows the failure once
        // the round trip is provably not converging.
        if (meAdminQuery.error instanceof OrgAdminUnauthorizedError) {
            reauthenticate(window.location.pathname);
        }
    }, [meAdminQuery.error, reauthenticate]);

    useEffect(() => {
        // Empty capability set ⇒ nothing in the console is theirs — back to the
        // launcher instead of an empty shell.
        // EAA-1814: `location.pathname !== '/'` guard added now that `/` lives
        // INSIDE this layout (App.tsx) — without it, a capability-less caller
        // already sitting on `/` re-triggered this same `navigate('/')` on every
        // render (a same-route no-op, but still a needless history replace).
        // The capability-less early return below is what removes the shell for
        // that caller; this effect now only matters for `/console/*` deep links.
        if (meAdminQuery.isSuccess && capabilities.length === 0 && location.pathname !== '/') {
            navigate('/', { replace: true });
        }
    }, [meAdminQuery.isSuccess, capabilities.length, navigate, location.pathname]);

    if (status === 'loading' || meAdminQuery.isLoading) {
        return (
            <>
                <AppBar />
                <main className="portal-console-loading" aria-busy="true">
                    {t('console.layout.loadingPermissions')}
                </main>
            </>
        );
    }

    if (meAdminQuery.isError && !(meAdminQuery.error instanceof OrgAdminUnauthorizedError)) {
        return (
            <>
                <AppBar />
                <main className="portal-console-loading">
                    <p className="portal-admin-error">{t('console.layout.permissionsError')}</p>
                </main>
            </>
        );
    }

    if (meAdminQuery.isSuccess && capabilities.length === 0) {
        // EAA-1814 (frame 11 "Plain user" — bug fix): `/` used to be a route
        // OUTSIDE this shell (a sibling in App.tsx), so a capability-less
        // caller never saw the sidebar at all. Once `/` moved to a CHILD route
        // of `ConsoleLayout` (App.tsx's own EAA-1814 comment on the route
        // move), that caller got the full admin chrome — sidebar, acting-org
        // card, nav-toggle — wrapped around `LauncherPage`'s plain-user hero,
        // which is exactly what frame 11 says NOT to render ("no console nav
        // at all"). This branch is the fix: skip the shell entirely and reuse
        // the pre-console `AppBar` + `.portal-launcher` page chrome instead,
        // so a plain user's `/` looks the same as it did before the route
        // moved. `ConsoleCapabilityProvider` is still mounted (capabilities is
        // always `[]` here) purely so `useCan()` behaves identically to every
        // other branch rather than falling back to context's default — see
        // consoleCapabilityContext.ts's own fail-closed default, which would
        // give the same answer anyway.
        return (
            <ConsoleCapabilityProvider capabilities={capabilities}>
                <AppBar />
                <main className="portal-launcher">
                    <Outlet />
                </main>
            </ConsoleCapabilityProvider>
        );
    }

    return (
        <ConsoleCapabilityProvider capabilities={capabilities}>
            <ActingOrgProvider organizations={meAdminQuery.data?.organizations ?? []}>
                <ConsoleBreadcrumbProvider>
                    <a className={styles.skipLink} href="#portal-console-main">
                        {t('console.layout.skipNav')}
                    </a>
                    {/* EAA-1813 (canonical shell): no global `<AppBar/>` here any more —
                    the sidebar (transparent, on the shell's own canvas tone) and the
                    rounded content panel (with its own ConsoleTopbar) replace it. */}
                    <div className={'portal-console' + (navOpen ? ' is-nav-open' : '')}>
                        <button
                            type="button"
                            className="portal-console-nav-toggle"
                            aria-expanded={navOpen}
                            aria-controls="portal-console-nav"
                            onClick={() => setNavOpen((open) => !open)}
                        >
                            <MenuIcon />
                            {navOpen ? t('console.layout.closeMenu') : t('console.layout.openMenu')}
                        </button>
                        {/* Backdrop only exists while open, so it can never swallow
                        clicks on the desktop layout. */}
                        {navOpen && (
                            <div
                                className="portal-console-nav-backdrop"
                                onClick={closeNav}
                                aria-hidden="true"
                            />
                        )}
                        {/* EAA-1814: frame 12 ("Org-admin") calls for a narrower 216px
                        sidebar (`.portal-console-nav-shell--org-admin`, already defined
                        in global.css but deliberately unwired) scoped to Tổng quan /
                        Ứng dụng / Tổ chức của tôi / Phân cấp only. `useCan()` only
                        exposes boolean capability flags — none of them distinguish an
                        org-admin caller from a super-admin one. `MeAdminData` DOES carry
                        `is_super_admin`, but decision D10 forbids deriving UI behaviour
                        from it client-side (capabilities[] is the only permission/role
                        source). Without a server-computed "is this caller scoped to one
                        org" signal (e.g. a dedicated capability or an explicit field
                        alongside `is_super_admin`), applying the org-admin variant here
                        would mean fabricating that distinction — so only the plain-user
                        vs admin split above is implemented; this shell renders the same
                        (super-admin-width) sidebar for every admin caller until that
                        signal exists. */}
                        <div
                            id="portal-console-nav"
                            className={
                                'portal-console-nav-shell' +
                                (navCollapsed ? ' portal-console-nav-shell--rail' : '')
                            }
                        >
                            <ConsoleNav
                                onNavigate={closeNav}
                                orgsCount={orgsCount}
                                roleMapIssueCount={roleMapIssueCount}
                                navCollapsed={navCollapsed}
                                onToggleCollapse={toggleNavCollapsed}
                                collapseLocked={collapseLocked}
                            />
                        </div>
                        {/* EAA-1813: the rounded, inset content panel (frame 09's
                        canonical geometry — see global.css). */}
                        <div className="portal-console-panel">
                            <ConsoleTopbar />
                            <main id="portal-console-main" className="portal-console-content">
                                <Outlet />
                            </main>
                        </div>
                    </div>
                </ConsoleBreadcrumbProvider>
            </ActingOrgProvider>
        </ConsoleCapabilityProvider>
    );
}

/* Inline nav icons — no icon dependency (design-pass rule). */
/* EAA-1813: nav-item icons are 17px — the literal size in canonical frame 09
   (handoff HTML lines 524/528/534/539/545/549/556/560/564: every nav row).
   COMPONENTS.md's Sidebar prose says 18px; frame 09 is canonical and wins.
   `MenuIcon` below stays 18px deliberately: it is the sub-720px drawer
   trigger, which appears in no design frame, so it has no canonical value. */
/* EAA-1813: the "về trang chính" exit link gets an icon like every other nav
   row now that the rail (72px) can only show icons — without one it would be
   the sole row that disappears entirely when collapsed. */
function HomeIcon() {
    return (
        <svg
            viewBox="0 0 24 24"
            width="17"
            height="17"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            aria-hidden="true"
        >
            <path
                d="m4 11 8-7 8 7v8a1 1 0 0 1-1 1h-4v-6H9v6H5a1 1 0 0 1-1-1v-8Z"
                strokeLinejoin="round"
            />
        </svg>
    );
}
/* EAA-1846 (frame 95 line 26): the collapsed rail's own expand button — a
   vertical bar + rightward arrow, not the box-with-divider glyph
   `ConsoleTopbar`'s old `PanelCollapseIcon` used (that one moved with the
   button; this is the design's own icon for the same action). */
function RailExpandIcon() {
    return (
        <svg
            viewBox="0 0 24 24"
            width="17"
            height="17"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            aria-hidden="true"
        >
            <path d="M4 5v14" strokeLinecap="round" />
            <path d="M10 12h9M15 8l4 4-4 4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}
/* EAA-1846 (frame 95 line 93): the expanded rail's own collapse button —
   mirror of `RailExpandIcon` (bar on the right, arrow pointing left). */
function RailCollapseIcon() {
    return (
        <svg
            viewBox="0 0 24 24"
            width="17"
            height="17"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            aria-hidden="true"
        >
            <path d="M20 5v14" strokeLinecap="round" />
            <path d="M14 12H5M9 8l-4 4 4 4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}
function MenuIcon() {
    return (
        <svg
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            aria-hidden="true"
        >
            <path d="M4 7h16M4 12h16M4 17h16" strokeLinecap="round" />
        </svg>
    );
}
function DirectoryIcon() {
    return (
        <svg
            viewBox="0 0 24 24"
            width="17"
            height="17"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            aria-hidden="true"
        >
            <path
                d="M4 5a2 2 0 0 1 2-2h4l2 2h6a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V5z"
                strokeLinejoin="round"
            />
        </svg>
    );
}
function GridIcon() {
    return (
        <svg
            viewBox="0 0 24 24"
            width="17"
            height="17"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            aria-hidden="true"
        >
            <rect x="3" y="3" width="7" height="7" rx="1.5" />
            <rect x="14" y="3" width="7" height="7" rx="1.5" />
            <rect x="3" y="14" width="7" height="7" rx="1.5" />
            <rect x="14" y="14" width="7" height="7" rx="1.5" />
        </svg>
    );
}
function BuildingIcon() {
    return (
        <svg
            viewBox="0 0 24 24"
            width="17"
            height="17"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            aria-hidden="true"
        >
            <path
                d="M3 21h18M6 21V5a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v16M14 21V9h3a1 1 0 0 1 1 1v11"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}
function TreeIcon() {
    return (
        <svg
            viewBox="0 0 24 24"
            width="17"
            height="17"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            aria-hidden="true"
        >
            <rect x="9" y="3" width="6" height="4" rx="1" />
            <rect x="3" y="17" width="6" height="4" rx="1" />
            <rect x="15" y="17" width="6" height="4" rx="1" />
            <path
                d="M12 7v4M6 17v-2a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v2M12 11v3"
                strokeLinecap="round"
            />
        </svg>
    );
}
function MapIcon() {
    return (
        <svg
            viewBox="0 0 24 24"
            width="17"
            height="17"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            aria-hidden="true"
        >
            <path d="M9 4 3 6v14l6-2 6 2 6-2V4l-6 2-6-2z" strokeLinejoin="round" />
            <path d="M9 4v14M15 6v14" />
        </svg>
    );
}
function UserIcon() {
    return (
        <svg
            viewBox="0 0 24 24"
            width="17"
            height="17"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            aria-hidden="true"
        >
            <circle cx="12" cy="8" r="3.5" />
            <path d="M5 20a7 7 0 0 1 14 0" strokeLinecap="round" />
        </svg>
    );
}
function ListIcon() {
    return (
        <svg
            viewBox="0 0 24 24"
            width="17"
            height="17"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            aria-hidden="true"
        >
            <path
                d="M8 6h13M8 12h13M8 18h13M3.5 6h.01M3.5 12h.01M3.5 18h.01"
                strokeLinecap="round"
            />
        </svg>
    );
}
function StorageIcon() {
    return (
        <svg
            viewBox="0 0 24 24"
            width="17"
            height="17"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            aria-hidden="true"
        >
            <ellipse cx="12" cy="6" rx="7" ry="3" />
            <path d="M5 6v6c0 1.7 3.1 3 7 3s7-1.3 7-3V6M5 12v6c0 1.7 3.1 3 7 3s7-1.3 7-3v-6" />
        </svg>
    );
}

function GearIcon() {
    return (
        <svg
            viewBox="0 0 24 24"
            width="17"
            height="17"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            aria-hidden="true"
        >
            <circle cx="12" cy="12" r="3" />
            <path
                d="M12 2v3m0 14v3M2 12h3m14 0h3M5 5l2 2m10 10 2 2M19 5l-2 2M7 17l-2 2"
                strokeLinecap="round"
            />
        </svg>
    );
}
